import { FC } from 'react';
import styled from 'styled-components';
import DateHeader from './DateHeader';
import ResidentRow from './ResidentRow';
import { ReactComponent as ChevronLeftIcon } from '../../assets/icons/chevron-left.svg';
import { ReactComponent as ChevronRightIcon } from '../../assets/icons/chevron-right.svg';
import { Player, CalendarData, DateSummary, CalendarResponse } from '../../services/Restorative';
import AccessibleHiddenText from '../AccessibleHiddenText';
import { RestorativeResidentData } from '../RestorativeResidentDialog/RestorativeResidentDialog';

const Table = styled.table(({ theme }) => {
  const { color } = theme.new;

  return `
    border-collapse: collapse;
    width: 100%;

    tr {
      border-bottom: 1px solid ${color.neutral[6]};
    }
  `;
});

const TableHead = styled.th(({ theme }) => {
  const { color, type } = theme.new;

  return `
    background-color: ${color.neutral[2]};
    color: ${color.shade[2]};
    font-size: ${type.size[14]};
    line-height: 1.375rem;
    font-weight: ${type.weight.bold};
    padding: 0.15625rem 0.5rem;
  `;
});

const DateHead = styled(TableHead)`
  width: 5%;
  padding: 0.15625rem 0.1875rem;
`;

const ButtonHead = styled(TableHead)`
  width: 3.25rem;
`;

const StatusHead = styled(TableHead)`
  width: 10%;
  text-align: center;
`;

const DateNavButton = styled.button(({ theme }) => {
  const { color } = theme.new;

  return `
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
    align-items: center;
    background-color: ${theme.new.color.neutral[1]};
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 6px;
    box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.5);

    &:active {
      box-shadow: 0px -1px 2px rgba(0, 0, 0, 0.5);
    }

    &:hover {
      background-color: ${color.neutral[2]};
    }

    svg {
      width: 0.75rem;
      height: 0.75rem;

      path {
        stroke: ${color.neutral[8]};
      }
    }
  `;
});

interface PlayerCalendarSummary {
  summary: DateSummary[];
  status: number;
}

interface RestorativeTableProps {
  players: Player[];
  dates: Date[];
  nextDateCallback: () => void;
  prevDateCallback: () => void;
  logClickCallback: (playerId: number, name: string, date: Date) => void;
  editClickCallback: (playerId: RestorativeResidentData) => void;
  nextEnabled: boolean;
  calendarInfo: CalendarResponse;
}

const RestorativeTable: FC<RestorativeTableProps> = ({
  players,
  dates,
  nextDateCallback,
  prevDateCallback,
  nextEnabled,
  logClickCallback,
  editClickCallback,
  calendarInfo,
}) => {
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}/${month}/${day}`;

    return formattedDate;
  };

  const parsePlayerDates = (calendarData: CalendarData): PlayerCalendarSummary => {
    if (typeof calendarData === 'undefined') {
      const emptySummary = dates.map((date) => {
        return { tasks: [], complete: false, date };
      });

      return { summary: emptySummary, status: 0 };
    }

    const summary: DateSummary[] = [];
    let status = 0;

    for (const date of dates) {
      const formattedDate = formatDate(date);
      const dailyInfo = calendarData[formattedDate];

      if (dailyInfo) {
        const dailyFinishedTasks: number[] = [];
        const programs = dailyInfo.programs;
        const completed = dailyInfo.completed;

        programs.forEach((program) => {
          dailyFinishedTasks.push(program.id);
        });

        if (completed) {
          status++;
        }

        summary.push({ tasks: dailyFinishedTasks, complete: completed, date });
      } else {
        summary.push({ tasks: [], complete: false, date });
      }
    }

    return { summary: summary, status: status };
  };

  const playerLoop = players.map((player) => {
    const calendarData = calendarInfo[player.id];
    const playerSummary = parsePlayerDates(calendarData?.calendar || undefined);

    return (
      <ResidentRow
        key={`residentRow-${player.id}`}
        id={player.id}
        name={player.name}
        programs={player.restorative_programs}
        status={playerSummary.status}
        dateSummaries={playerSummary.summary}
        logClickCallback={logClickCallback}
        editClickCallback={editClickCallback}
      />
    );
  });

  const dateLoop = dates.map((date) => {
    return (
      <DateHead scope="col" key={`dateHeader-${date}`}>
        <DateHeader date={date} />
      </DateHead>
    );
  });

  return (
    <Table>
      <thead>
        <tr>
          <TableHead scope="col">Residents</TableHead>

          <TableHead scope="col">Today's progress</TableHead>

          <ButtonHead></ButtonHead>
          <ButtonHead></ButtonHead>

          <ButtonHead scope="col">
            <DateNavButton onClick={prevDateCallback}>
              <ChevronLeftIcon />
              <AccessibleHiddenText>Navigate one day back</AccessibleHiddenText>
            </DateNavButton>
          </ButtonHead>

          {dateLoop}

          <ButtonHead scope="col">
            {nextEnabled && (
              <DateNavButton onClick={nextDateCallback}>
                <ChevronRightIcon />
                <AccessibleHiddenText>Navigate one day forward</AccessibleHiddenText>
              </DateNavButton>
            )}
          </ButtonHead>

          <StatusHead scope="col">7 day status</StatusHead>
        </tr>
      </thead>

      <tbody>{playerLoop}</tbody>
    </Table>
  );
};

export default RestorativeTable;
